import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getHelpCenterGroups } from '../../help/registry';
import Icon from '../Icon';
import HelpModal from './HelpModal';
import './HelpCenterMenu.css';

/**
 * NER-1276 — Capa 3: Help Center dropdown in the Header.
 *
 * Replaces the old hardcoded `startTour('dashboard')` button. Lists every
 * registered topic grouped by feature; selecting one opens the HelpModal.
 */
function HelpCenterMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState(null);
  const containerRef = useRef(null);

  const groups = getHelpCenterGroups();

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const openTopic = (topicKey) => {
    setActiveTopic(topicKey);
    setOpen(false);
  };

  // Feature label falls back to the raw feature key if no i18n entry exists.
  const featureLabel = (feature) => t(`help.features.${feature}`, feature);

  return (
    <div className="help-center-container" ref={containerRef}>
      <button
        type="button"
        className={`header-icon-btn header-help-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={t('help.center.title')}
        aria-expanded={open}
        title={t('help.center.title')}
      >
        <Icon name="help_outline" size="small" />
      </button>

      {open && (
        <div className="help-center-dropdown">
          <div className="help-center-header">{t('help.center.title')}</div>
          <div className="help-center-list">
            {groups.map(({ feature, topics }) => (
              <div className="help-center-group" key={feature}>
                <div className="help-center-group-title">{featureLabel(feature)}</div>
                {topics.map((topic) => (
                  <button
                    type="button"
                    key={topic.topicKey}
                    className="help-center-item"
                    onClick={() => openTopic(topic.topicKey)}
                  >
                    <Icon name="article" size="small" className="help-center-item-icon" />
                    <span>{t(topic.titleKey)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTopic && <HelpModal topicKey={activeTopic} onClose={() => setActiveTopic(null)} />}
    </div>
  );
}

export default HelpCenterMenu;
